import { useEffect, useState } from 'react';
import axios from 'axios';
import GameLabel from '@/app/Components/GameLabel.jsx';
import GTMEventManager from '@/app/GTMEventManager.js';
import { useTranslations } from '@/app/i18n.js';
import BookingTimeSlot from './BookingTimeSlot.jsx';
import GameDetailsModal from './GameDetailsModal.jsx';
import { loadAlternatives } from './bookingAlternatives.js';

function GameRating({ value }) {
    const score = Math.min(5, Math.max(0, Number(value) || 0));
    const label = `${Number(score.toFixed(1))}/5`;

    return <div className="alternative-game-rating flex shrink-0 items-center gap-2" aria-label={label}>
        <span className="relative inline-block leading-none tracking-[0.08em]" aria-hidden="true">
            <span className="text-medium-gray">★★★★★</span>
            <span className="absolute inset-y-0 left-0 overflow-hidden whitespace-nowrap text-c-yellow"
                style={{ width: `${score * 20}%` }}>★★★★★</span>
        </span>
        <span className="text-sm font-semibold tabular-nums">{label}</span>
    </div>;
}

export default function AlternativeGames({ pointOfSale, game, games = [], date, team, locale, onSelect, busy = false }) {
    const __ = useTranslations();
    const [state, setState] = useState({ status: 'loading', items: [] });
    const [retry, setRetry] = useState(0);
    const [selection, setSelection] = useState(null);
    const [detailsGame, setDetailsGame] = useState(null);
    useEffect(() => {
        setState({ status: 'loading', items: [] });
        setSelection(null);
        setDetailsGame(null);
        return loadAlternatives(axios.get, route('booking.alternatives', { point_of_sale_slug: pointOfSale.slug, game: game.slug }),
            { date, players: team },
            (items) => setState({ status: 'ready', items }),
            () => setState({ status: 'error', items: [] }));
    }, [pointOfSale.slug, game.slug, date, team, retry]);
    if (state.status === 'ready' && state.items.length === 0) return null;

    const localized = (text) => typeof text === 'string' ? text : text?.[locale] ?? text?.fr ?? '';
    const buttonStyle = 'min-h-[44px] border border-gray-500 dark:border-light-gray px-4 py-2 disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2';
    return (
        <section id="alternative-games" className="scroll-mt-40 mt-2 mb-4 pb-6 text-black dark:text-white" aria-labelledby="alternatives-title" aria-busy={state.status === 'loading' || busy}>
            <div className="flex flex-col gap-10 dark:px-4 pt-6 pb-3 lg:pt-8 dark:sm:px-6 dark:3xl:px-8" id="time-picker">
                <h4 id="alternatives-title" tabIndex={-1} className="text-black dark:text-white text-md sm:text-lg 3xl:text-xl font-semibold">
                    {__('book.alternatives.title')}
                </h4>
            </div>
            <div className="alternative-games-container px-4">
                {state.status === 'loading' && <p role="status">{__('book.alternatives.loading')}</p>}
                {state.status === 'error' && <div role="alert"><p className="mb-3">{__('book.alternatives.error')}</p><button type="button" className={buttonStyle} onClick={() => setRetry(retry + 1)}>{__('book.alternatives.retry')}</button></div>}
                {state.status === 'ready' && state.items.length > 0 && <div className="alternative-games-grid grid gap-6 items-start">
                    {state.items.map(({ game: alternative, slots, price_levels }) => (
                        <article key={alternative.id} className="alternative-game-card min-w-0 overflow-hidden rounded border border-gray-500 dark:border-light-gray" aria-labelledby={`alternative-${alternative.id}`}>
                            <div className={`alternative-game-header ${alternative.main_poster || alternative.card ? '' : 'alternative-game-header-text-only'}`}>
                                {(alternative.main_poster || alternative.card) && <div className={`alternative-game-image relative overflow-hidden rounded-sm bg-black ${alternative.main_poster && alternative.card ? 'alternative-game-image-switchable' : ''}`}>
                                    {alternative.main_poster && <img src={alternative.main_poster} alt="" className={`alternative-game-poster absolute inset-0 w-full h-full object-cover ${alternative.card ? '' : 'alternative-game-only-image'}`} loading="lazy" />}
                                    {alternative.card && <img src={alternative.card} alt="" className={`alternative-game-card-image absolute inset-0 w-full h-full object-cover ${alternative.main_poster ? '' : 'alternative-game-only-image'}`} loading="lazy" />}
                                    {alternative.logo && <>
                                        <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/70 to-transparent" aria-hidden="true" />
                                        <img src={alternative.logo} alt="" className="absolute bottom-2 left-1/2 -translate-x-1/2 w-8/12 h-2/5 object-contain" loading="lazy" />
                                    </>}
                                </div>}
                                <div className="alternative-game-copy min-w-0">
                                    <div className="alternative-game-meta relative mb-3 flex items-center justify-between gap-3">
                                        <GameLabel style={{ position: 'relative', top: 0, left: 0, display: 'inline-block', lineHeight: 1.1, fontSize: '0.75rem' }}>{alternative.category
                                            ? `${localized(alternative.category.title_1)} ${localized(alternative.category.title_2)}`.trim()
                                            : localized(alternative.genre)}</GameLabel>
                                        <GameRating value={alternative.editor_score} />
                                    </div>
                                    <h3 id={`alternative-${alternative.id}`} className="font-semibold text-lg leading-snug break-words">{alternative.name}</h3>
                                    <p className="text-sm leading-relaxed mt-1 break-words">{localized(alternative.catch_phrase)}</p>
                                    <button
                                        type="button"
                                        className="mt-3 inline-flex min-h-[32px] items-center rounded border border-gray-500 dark:border-light-gray px-3 py-2 text-xs font-medium transition-colors hover:border-primary hover:text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                                        onClick={() => {
                                            const details = games.find(({ id }) => id === alternative.id) ?? alternative;
                                            setDetailsGame(details);
                                            GTMEventManager.ecommerce().viewItem(details);
                                        }}
                                    >
                                        {__('book.alternatives.learn_more')}
                                    </button>
                                </div>
                            </div>
                            <p className="sr-only">{__('book.alternatives.per_player')}</p>
                            <div className="grid alternative-slots border-t border-gray-500 dark:border-light-gray">
                                {slots.map((choice) => {
                                    const key = `${alternative.id}:${choice.time}`;
                                    return <BookingTimeSlot key={key} time={choice.time} slot={choice.slot}
                                                            available selected={selection === key} disabled={busy} prices={price_levels}
                                                            remaining={choice.remaining_seats} capacity={choice.slot.max_seats}
                                                            onSelect={() => { setSelection(key); onSelect(alternative, choice); }} />;
                                })}
                            </div>
                        </article>
                    ))}
                </div>}
            </div>
            <GameDetailsModal game={detailsGame} locale={locale} onClose={() => setDetailsGame(null)} />
        </section>
    );
}
